import { ChangelogService } from '../api/services/ChangelogService.js';
import { FeedbackService } from '../api/services/FeedbackService.js';
import { HelpService } from '../api/services/HelpService.js';
import { LiveChatService } from '../api/services/LiveChatService.js';
import { SurveyService } from '../api/services/SurveyService.js';
import { getDeviceInfo } from '../api/utils/helpers.js';
import { BaseAPIService } from './BaseAPIService.js';
import { isContactBlockedError } from '../utils/errors.js';
import { getIpInfo } from '../utils/getIpInfo.js';
import { getBrowserInfo } from '../utils/helpers.js';

const UNBLOCKED_STATE = {
	isBlocked: false,
	supportBlocked: false,
	feedbackBlocked: false,
};

export class APIService extends BaseAPIService {
	constructor(config = {}) {
		super(config);

		this.feedback = new FeedbackService(this);
		this.survey = new SurveyService(this);
		this.liveChat = new LiveChatService(this);
		this.help = new HelpService(this);
		this.changelog = new ChangelogService(this);

		this.blockState = { ...UNBLOCKED_STATE };
		this.onBlockStateChange = null;
	}

	getBlockState() {
		return { ...this.blockState };
	}

	_setBlockState(next) {
		const merged = { ...this.blockState, ...next };
		merged.isBlocked = merged.supportBlocked || merged.feedbackBlocked;

		const changed = Object.keys(merged).some(
			(key) => merged[key] !== this.blockState[key]
		);
		if (!changed) return;

		this.blockState = merged;
		if (typeof this.onBlockStateChange === 'function') {
			this.onBlockStateChange(this.getBlockState());
		}
	}

	// Identify replaces the whole state, so an admin unblock is picked up too.
	_applyIdentifyBlockState(response) {
		const user = response?.user || response?.data;
		if (!user || typeof user !== 'object') return;

		// Older responses only carry is_blocked, which meant a Support block.
		const supportBlocked = Boolean(user.support_blocked ?? user.is_blocked);
		const feedbackBlocked = Boolean(user.feedback_blocked);
		this._setBlockState({ supportBlocked, feedbackBlocked });
	}

	// A 403 CONTACT_BLOCKED on a write is the source of truth even when
	// identify never ran this session (e.g. a returning pre-chat visitor).
	async _guardBlocked(side, request) {
		try {
			return await request();
		} catch (error) {
			if (isContactBlockedError(error)) {
				this._setBlockState({ [`${side}Blocked`]: true });
			}
			throw error;
		}
	}

	clearSession() {
		super.clearSession();
		this._setBlockState({ ...UNBLOCKED_STATE });
	}

	async submitFeedback(data) {
		return this._guardBlocked('feedback', () =>
			this.feedback.submitFeedback(data)
		);
	}

	async getActiveSurveys(context) {
		return this.survey.getActiveSurveys(context);
	}

	async submitSurveyResponse(surveyId, responseData) {
		return this._guardBlocked('feedback', () =>
			this.survey.submitSurveyResponse(surveyId, responseData)
		);
	}

	async dismissSurvey(surveyId) {
		return this.survey.dismissSurvey(surveyId);
	}

	async getLiveChatSettings() {
		return this.liveChat.getLiveChatSettings();
	}

	async checkAgentsOnline() {
		return this.liveChat.checkAgentsOnline();
	}

	async getConversations(options) {
		return this.liveChat.getConversations(options);
	}

	async getConversation(conversationId) {
		return this.liveChat.getConversation(conversationId);
	}

	async getMessages(conversationId, options) {
		return this.liveChat.getMessages(conversationId, options);
	}

	async startConversation(data) {
		return this._guardBlocked('support', () =>
			this.liveChat.startConversation(data)
		);
	}

	async sendMessage(conversationId, data) {
		return this._guardBlocked('support', () =>
			this.liveChat.sendMessage(conversationId, data)
		);
	}

	async uploadFile(base64Data, filename) {
		return this._guardBlocked('support', () =>
			this.liveChat.uploadFile(base64Data, filename)
		);
	}

	async sendTypingIndicator(conversationId, isTyping) {
		return this._guardBlocked('support', () =>
			this.liveChat.sendTypingIndicator(conversationId, isTyping)
		);
	}

	async markConversationAsRead(conversationId) {
		return this.liveChat.markConversationAsRead(conversationId);
	}

	async getUnreadCount() {
		return this.liveChat.getUnreadCount();
	}

	async submitRating(conversationId, data) {
		return this._guardBlocked('support', () =>
			this.liveChat.submitRating(conversationId, data)
		);
	}

	async identify(metadata) {
		await this._ensureSession();

		if (this.mock) {
			await new Promise((r) => setTimeout(r, 300));
			const mockResponse = {
				status: true,
				data: {
					contact_id: 'mock_contact_' + Date.now(),
					email: metadata.email || '',
					name: metadata.name || '',
					avatar: metadata.avatar || '',
					is_new: true,
				},
			};
			this._storeContactIdentity(mockResponse.data, metadata);
			return mockResponse;
		}

		const payload = {};

		if (metadata.email) payload.email = metadata.email;
		if (metadata.user_id) payload.user_id = metadata.user_id;
		if (metadata.name) payload.name = metadata.name;
		if (metadata.phone) payload.phone = metadata.phone;
		// Top-level standard CRM properties, not folded into attributes.
		if (metadata.job_title) payload.job_title = metadata.job_title;
		if (metadata.gender) payload.gender = metadata.gender;
		if (metadata.avatar) payload.avatar = metadata.avatar;
		if (metadata.attributes) payload.attributes = metadata.attributes;
		if (metadata.company) payload.company = metadata.company;

		const ipInfo = await getIpInfo();
		if (ipInfo) payload.ip_info = ipInfo;

		// Browser-reported language/timezone are more accurate than ip_info's geo-IP-derived ones.
		try {
			const browserInfo = getBrowserInfo();
			if (browserInfo?.language) payload.language = browserInfo.language;
			if (browserInfo?.timezone) payload.timezone = browserInfo.timezone;
		} catch {
			/* best-effort */
		}

		const deviceInfo = getDeviceInfo();
		if (deviceInfo) payload.device_info = deviceInfo;

		const response = await this._makeRequest('/widget/identify', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.sessionToken}`,
			},
			body: JSON.stringify(payload),
		});

		if (response?.status && response?.data) {
			this._storeContactIdentity(response.data, metadata);
		}

		this._applyIdentifyBlockState(response);

		return response;
	}

	_storeContactIdentity(data, metadata = {}) {
		this.contactId = data.contact_id || null;
		this.contactEmail = data.email || metadata.email || null;
		this.contactName = data.name || metadata.name || null;

		try {
			localStorage.setItem(
				'product7_contact',
				JSON.stringify({
					contactId: this.contactId,
					contactEmail: this.contactEmail,
					contactName: this.contactName,
				})
			);
		} catch (e) {
			/* silent */
		}
	}

	getContactIdentity() {
		if (this.contactId) {
			return {
				contactId: this.contactId,
				contactEmail: this.contactEmail,
				contactName: this.contactName,
			};
		}

		try {
			const stored = localStorage.getItem('product7_contact');
			if (stored) {
				const parsed = JSON.parse(stored);
				this.contactId = parsed.contactId;
				this.contactEmail = parsed.contactEmail;
				this.contactName = parsed.contactName;
				return parsed;
			}
		} catch (e) {
			/* silent */
		}

		return null;
	}

	async identifyContact(data) {
		return this.identify(data);
	}

	async getHelpCollections(options) {
		return this.help.getHelpCollections(options);
	}

	async searchHelpArticles(query, options) {
		return this.help.searchHelpArticles(query, options);
	}

	async getChangelogs(options) {
		return this.changelog.getChangelogs(options);
	}
}
